/** A cave definition at one level becomes a grid the engine can run (docs/spec/03 §1). */

import { E } from '../sim/elements';
import { Rng } from '../sim/rng';
import { DEFAULT_TICKS } from '../sim/tuning';
import { LEGEND } from './legend';
import type { BuiltCave, CaveDef, CaveFill, CaveLevel } from './types';

const DEFAULT_FILL: CaveFill = { base: '.', items: [] };

/** The level's numbers; a cave with fewer than five levels repeats its last. */
export function levelOf(def: CaveDef, level: number): CaveLevel {
  const i = Math.min(Math.max(1, Math.floor(level)), 5) - 1;
  return def.levels[Math.min(i, def.levels.length - 1)] ?? { seed: 1, diamonds: 1, time: 999 };
}

/** One `?` cell: a roll in thousandths against the cumulative item percentages. */
function roll(rng: Rng, fill: CaveFill): number {
  const r = rng.int(1000);
  let acc = 0;
  for (const [ch, percent] of fill.items) {
    acc += Math.round(percent * 10);
    if (r < acc) return LEGEND[ch] ?? E.DIRT;
  }
  return LEGEND[fill.base] ?? E.DIRT;
}

export function mapSize(def: CaveDef): { w: number; h: number } {
  let w = 0;
  for (const row of def.map) w = Math.max(w, row.length);
  return { w, h: def.map.length };
}

export function buildCave(def: CaveDef, level: number): BuiltCave {
  const lv = levelOf(def, level);
  const lvl = Math.min(Math.max(1, Math.floor(level)), 5);
  const { w, h } = mapSize(def);
  const fill = def.fill ?? DEFAULT_FILL;
  const rng = new Rng(lv.seed);
  const cells = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    const row = def.map[y] ?? '';
    for (let x = 0; x < w; x++) {
      const ch = row[x] ?? 'W';
      // The roll happens for every `?` in reading order, so a level's layout depends only on its seed.
      cells[y * w + x] = ch === '?' ? roll(rng, fill) : (LEGEND[ch] ?? E.STEEL);
    }
  }
  return {
    def,
    level: lvl,
    colors: def.colors,
    w,
    h,
    cells,
    params: {
      tickMs: lv.tick ?? DEFAULT_TICKS[lvl - 1] ?? 150,
      diamondsNeeded: lv.diamonds,
      diamondValue: def.diamondValue,
      extraValue: def.extraValue,
      time: lv.time,
      amoebaTime: def.amoebaTime ?? 0,
      magicWallTime: def.magicWallTime ?? 0,
      level: lvl,
      intermission: def.intermission,
    },
  };
}
